<template>
  <div class="auth-root">
    <b-spinner 
      v-if="isLoading" 
      class="d-block mx-auto text-primary my-2" 
    />
    <auth-layout 
      v-else 
      :community="community"
      :auth-api-url="authApiUrl"
    >
      <router-view />
    </auth-layout>
  </div>
</template>

<script>
import { selectLangFromCommunity } from '@/@core/utils/collective'
import { translateTranslationTable } from '@/@core/libs/i18n/utils'
import { AccessTokenKey } from '@/store/auth/auth.store.constants'
import AuthLayout from './layouts/AuthLayout.vue'
import { mapGetters } from 'vuex'
import { getImageResource } from '@/@core/utils/image-utils';

const COMMUNITY_KEYS = {
  NECTIOS: 'a37e7be4-2783-4605-823c-12b4d308e503',
  NECTIOS_USER_GUIDE: 'e837e360-9034-11ee-9132-6f64d2d05b54',
  INHOSPITECNIA: 'f6d4dec0-e1ec-11ec-ac71-cd2eb883fdc3',
  BARCELONACTIVA: 'CXBT3al0hR',
  COEIC: '8f30c710-a169-11ed-88c5-c1eb2d036009',
  NEXOS: 'f3166db0-b635-11ef-805d-f79ce25412de',
  INTERCONTINENTAL_ALLIANCE: '6725a490-2547-11ee-a8ab-dfc8db3ddec2',
  CONEXION_ESPANA: '7c23efd0-9d5e-11ed-804a-650ff09867b0',
  GOVTECH_GENCAT: '33cc11c0-7d46-11f0-9889-e564706f373f'
}

export default {
  name: 'AuthRoot',

  components: {
    AuthLayout
  },

  data() {
    return {
      isLoading: true,
      community: null
    }
  },

  computed: {
    ...mapGetters([
      'collective',
      'mainCollective',
      'currentCollective',
      'collectiveFavicon',
    ]),
    faviconURL() {
      // console.log('faviconURL - this.community:', this.community);

      // Enhanced favicon resolution with better error handling
      try {
        if(this.community){
          // First try to get the favicon from community customization
          // console.log('Do we have a favicon in this.community.customization.theme.favicon?');
          if (this.community?.customization?.theme?.favicon) {
            const favicon = this.loadImageResource(this.community.customization.theme.favicon);
            if (favicon){
              // console.log('favicon found in this.community.customization.theme.favicon', favicon);
              return favicon;
            }
          }

          // Then try the community logo from customizations
          // console.log('Do we have a community logo in this.community.customization.logo_url?');
          if (this.community?.customization?.logo_url) {
            const customLogo = this.loadImageResource(this.community.customization.logo_url);
            if (customLogo){
              // console.log('customLogo found in this.community.customization.logo_url', customLogo);
              return customLogo;
            }
          }

          // Then try the community logo from getSpaceLogoImage
          // console.log('Do we have a community logo in this.getSpaceLogoImage(this.community)?');
          const spaceLogo = this.getSpaceLogoImage(this.community);
          if (spaceLogo){
            // console.log('spaceLogo found in this.getSpaceLogoImage(this.community)', spaceLogo);
            return spaceLogo;
          }

          // Then try the community logo
          // console.log('Do we have a community logo in this.community.logoURL?');
          if (this.community?.logoURL) {
            const logo = this.loadImageResource(this.community.logoURL);
            if (logo){
              // console.log('logo found in this.community.logoURL', logo);
              return logo;
            }
          }

          // Then try the login logo from theme
          // console.log('Do we have a login logo in this.community.customization.theme.login.logo?');
          if (this.community?.customization?.theme?.login?.logo) {
            const loginLogo = this.loadImageResource(this.community.customization.theme.login.logo);
            if (loginLogo){
              // console.log('loginLogo found in this.community.customization.theme.login.logo', loginLogo);
              return loginLogo;
            }
          }

          // Then try header logo
          // console.log('Do we have a header logo in this.community.customization.auth_header_image?');
          if (this.community?.customization?.auth_header_image) {
            const headerLogo = this.loadImageResource(this.community.customization.auth_header_image);
            if (headerLogo){
              // console.log('headerLogo found in this.community.customization.auth_header_image', headerLogo);
              return headerLogo;
            }
          }
        } else {
          // console.log('No community found, fallback to default favicon');
        }

        // Customer-specific defaults
        // console.log('Do we have a nexos logo for Nexos?');
        if (this.isNexos) {
          // console.log('nexos logo found in /assets/customers/nexos/nexos-auth-header.png');
          return '/assets/customers/nexos/icons/favicon.svg';
        }
        if (this.isIntercontinentalAlliance) {
          // console.log('intercontinental alliance logo found in /assets/images/brand/intercontinental-alliance-favicon.png');
          return '/assets/images/brand/intercontinental-alliance-favicon.png';
        }
        if (this.isCoeic) {
          // console.log('coeic logo found in /assets/images/brand/coeic-favicon.png');
          return '/assets/images/brand/coeic-favicon.png';
        }

        // Finally fallback to default favicon
        // console.log('finally fallback to default favicon found in /assets/images/brand/nectios-favicon.png');
        return process.env.VUE_APP_FAVICON || '/assets/images/brand/nectios-favicon.png';
      } catch (error) {
        console.error('Error resolving favicon:', error);
        return process.env.VUE_APP_FAVICON || '/assets/images/brand/nectios-favicon.png';
      }
    },
    communitySlug() {
      const slugParam = this.$route.params.communityId;
      
      // Enhanced validation
      if (!slugParam || 
          slugParam === 'undefined' || 
          slugParam === 'null' || 
          slugParam === 'error' || 
          slugParam === '') {
        // console.log('AuthRoot - Invalid or missing community slug, using empty string');
        return '';
      }
      
      return slugParam;
    },
    authApiUrl() {
      if(this.useNewAuthApi){
        return 'https://api.nectios.com/api/v1/';
      }
      return (process.env.VUE_APP_AUTH_API_URL || 'https://auth-api.nectios.com') + '/v1/auth/';
    },
    // Common computed properties for auth pages
    isIntercontinentalAlliance() {
      const isIntercontinentalKey = this.community?.key === COMMUNITY_KEYS.INTERCONTINENTAL_ALLIANCE;
      if(isIntercontinentalKey){
        return true;
      }
      // check the current url
      const isDomainIntercontinental = window.location.hostname.includes('intercontinentalalliance.com');
      return isDomainIntercontinental;
    },
    isCoeic() {
      const isCoeicKey = this.community?.key === COMMUNITY_KEYS.COEIC;
      if(isCoeicKey){
        return true;
      }
      return false;
    },
    isNexos() {
      // slug: nexos
      return process.env.VUE_APP_CUSTOMER === 'nexos' || this.community?.key === COMMUNITY_KEYS.NEXOS;
    },
    isInhospitecnia() {
      // slug: hospitecnia
      return this.community?.key === COMMUNITY_KEYS.INHOSPITECNIA;
    },
    isConexionEspana() {
      // slug: conexion-espana
      return this.community?.key === COMMUNITY_KEYS.CONEXION_ESPANA;
    },
    isNectios() {
      // slug: nectios
      return this.community?.key === COMMUNITY_KEYS.NECTIOS;
    },
    isNectiosUserGuide() {
      // slug: nectios-user-guide
      return this.community?.key === COMMUNITY_KEYS.NECTIOS_USER_GUIDE;
    },
    isGovtechGencat() {
      // slug: govtech-gencat
      return this.community?.key === COMMUNITY_KEYS.GOVTECH_GENCAT;
    },
    useNewAuthApi() {
      return this.isNectios || this.isNectiosUserGuide;
    },
    mainSpace() {
      return this.mainCollective.key ? this.mainCollective : this.currentCollective;
    },
    supportEmail() {
      if(this.isNexos){
        return process.env.VUE_APP_SUPPORT_EMAIL || 'comunidad@nexos.infobancolombia.com';
      }
      if(this.isIntercontinentalAlliance){
        return 'support@intercontinentalalliance.com';
      }
      if(this.isInhospitecnia){
        return 'info@hospitecnia.com';
      }
      return process.env.VUE_APP_SUPPORT_EMAIL || 'support@nectios.com';
    },
    // Add new computed properties for metadata
    metadataTitle() {
      if(this.isIntercontinentalAlliance){
        return 'Intercontinental Alliance';
      }
      if(this.isCoeic){
        return 'COEIC';
      }
      if(this.isNexos){
        return 'Nexos-Bancolombia';
      }
      // Ensure we have a valid title, falling back through multiple options
      const communityName = this.translate(this.community?.name || '');
      const mainSpaceName = this.translate(this.mainSpace?.name || '');
      const defaultTitle = 'Nectios';
      return communityName || mainSpaceName || defaultTitle;
    },
    metadataDescription() {
      return this.mainSpace?.headMeta?.name_description || '';
    },
    metadataKeywords() {
      return this.mainSpace?.headMeta?.name_keywords || '';
    },
    metadataAuthor() {
      return "Nectios";
      // return this.mainSpace?.headMeta?.name_author || '';
    },
    metadataTwitterTitle() {
      if(this.isIntercontinentalAlliance){
        return 'Intercontinental Alliance';
      }
      return this.mainSpace?.headMeta?.name_twitter_title || this.metadataTitle;
    },
    metadataTwitterDescription() {
      return this.mainSpace?.headMeta?.name_twitter_description || this.metadataDescription;
    },
    metadataOgTitle() {
      if(this.isIntercontinentalAlliance){
        return 'Intercontinental Alliance';
      }
      return this.mainSpace?.headMeta?.property_og_title || this.metadataTitle;
    },
    metadataOgDescription() {
      return this.mainSpace?.headMeta?.property_og_description || this.metadataDescription;
    },
    currentLocale() {
      return this.$store.state.locale.currentLocale;
    }
  },

  async created() {
    // console.log('AuthRoot - initCommunity started');
    // console.log('AuthRoot - communitySlug:', this.communitySlug, 'type:', typeof this.communitySlug);
    
    // Set initial default metadata
    if(!this.isNexos){
      this.setMetadata();
    }
    
    if(!this.communitySlug || this.communitySlug === ''){
      this.isLoading = false;
      // console.log('AuthRoot - initCommunity - no valid community slug found, setting isLoading to false');
      // Set a minimal default community object
      this.community = {
        name: this.isNexos ? 'Nexos' : 'Nectios',
        slug: '',
        customization: {
          theme: { login: { backgrounds: [] } },
          auth_image_url: {},
          auth_header_image: null
        }
      };
      // Update metadata with default values
      this.setMetadata();
      return;
    }

    try {
      const authApiUrl = this.authApiUrl;
      // console.log('AuthRoot - initCommunity - fetching community data from:', `${authApiUrl}login?communitySlug=${this.communitySlug}`);
      
      // console.log('AuthRoot - initCommunity - fetching community data from:', `${authApiUrl}login?communitySlug=${this.communitySlug}`);
      const response = await fetch(
        `${authApiUrl}login?communitySlug=${this.communitySlug}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...this.getAuthHeaders()
          },
          credentials: process.env.NODE_ENV === 'production' ? 'same-origin' : 'include',
          mode: 'cors'
        }
      );
      
      // console.log('AuthRoot - initCommunity - response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('AuthRoot - initCommunity - error response:', errorData);
        
        if (response.status === 401 || response.status === 403) {
          // console.log('AuthRoot - initCommunity - unauthorized, logging out');
          this.$store.dispatch('logout');
          this.$router.push({
            name: 'auth-login',
            params: { communityId: this.communitySlug }
          });
          return;
        }
        throw new Error(errorData?.message || `Failed to fetch community data: ${response.status}`);
      } else {
        // console.log('AuthRoot - initCommunity - response ok:', response);
      }

      const data = await response.json();
      // console.log('AuthRoot - initCommunity - received data:', data);
      
      if (!data || !data.data) {
        console.error('AuthRoot - initCommunity - invalid response format:', data);
        throw new Error('Invalid response format from server');
      }

      this.community = data.data;
      
      // Ensure there's a customization object to avoid null reference errors
      if (!this.community.customization) {
        // console.log('AuthRoot - initCommunity - adding default customization object');
        this.community.customization = {
          theme: {
            login: {
              backgrounds: []
            }
          },
          auth_image_url: {},
          auth_header_image: null
        };
      }

      // Set language
      selectLangFromCommunity(this.community);

      // Update metadata with community data
      this.setMetadata();

    } catch (error) {
      console.error('AuthRoot - initCommunity - error fetching community:', error);
      if (error.message.includes('Failed to fetch')) {
        console.error('AuthRoot - initCommunity - CORS or Network Error - Please check API availability and CORS configuration');
      }
      
      // Set a default community object to avoid undefined errors
      this.community = {
        name: this.communitySlug,
        slug: this.communitySlug
      };
      
      // Update metadata with fallback values
      this.setMetadata();
      
      // console.log('AuthRoot - initCommunity - using fallback community:', this.community);
    } finally {
      // console.log('AuthRoot - initCommunity - completed, isLoading set to false');
      this.isLoading = false;
    }
  },

  methods: {
    getAuthHeaders() {
      const token = this.$cookies.get(AccessTokenKey);
      return token ? { 'Authorization': `Bearer ${token}` } : {};
    },

    loadImageResource(resource) {
      if (!resource) return null;
      if (typeof resource === 'string') return resource;
      if (resource.url) return resource.url;
      if (resource.path) return resource.path;
      return null;
    },

    // Add getSpaceLogoImage as a method
    getSpaceLogoImage(space) {
      // console.log('getSpaceLogoImage - space:', space);
      if (!space) return '';
      if (space.logoURL) {
        return getImageResource(space.logoURL);
      }
      if (space.header?.logo_url) {
        return getImageResource(space.header.logo_url);
      }
      return '';
    },

    // Separate method for setting metadata
    setMetadata() {
      try {
        // Basic meta tags with enhanced fallbacks
        const metaTags = {
          title: this.metadataTitle,
          description: this.metadataDescription || process.env.VUE_APP_DESCRIPTION || 'Nectios - Community Platform',
          keywords: this.metadataKeywords || process.env.VUE_APP_KEYWORDS || '',
          author: this.metadataAuthor || process.env.VUE_APP_AUTHOR || 'Nectios'
        };

        // Set document title
        document.title = metaTags.title;

        // Update basic meta tags with error handling
        Object.entries(metaTags).forEach(([name, content]) => {
          if (content) {
            try {
              let element = document.querySelector(`meta[name="${name}"]`);
              if (!element && name !== 'title') {
                element = document.createElement('meta');
                element.setAttribute('name', name);
                document.head.appendChild(element);
              }
              if (element) {
                element.setAttribute('content', content);
              }
            } catch (error) {
              console.error(`Error setting ${name} meta tag:`, error);
            }
          }
        });

        // Update Open Graph meta tags with error handling
        const ogTags = {
          'og:title': this.metadataOgTitle || metaTags.title,
          'og:description': this.metadataOgDescription || metaTags.description,
          'og:type': 'website',
          'og:url': window.location.href,
          'og:image': this.faviconURL
        };

        Object.entries(ogTags).forEach(([property, content]) => {
          if (content) {
            try {
              let element = document.querySelector(`meta[property="${property}"]`);
              if (!element) {
                element = document.createElement('meta');
                element.setAttribute('property', property);
                document.head.appendChild(element);
              }
              element.setAttribute('content', content);
            } catch (error) {
              console.error(`Error setting ${property} meta tag:`, error);
            }
          }
        });

        // Update Twitter Card meta tags with error handling
        const twitterTags = {
          'twitter:card': 'summary',
          'twitter:title': this.metadataTwitterTitle || metaTags.title,
          'twitter:description': this.metadataTwitterDescription || metaTags.description,
          'twitter:image': this.faviconURL
        };

        Object.entries(twitterTags).forEach(([name, content]) => {
          if (content) {
            try {
              let element = document.querySelector(`meta[name="${name}"]`);
              if (!element) {
                element = document.createElement('meta');
                element.setAttribute('name', name);
                document.head.appendChild(element);
              }
              element.setAttribute('content', content);
            } catch (error) {
              console.error(`Error setting ${name} meta tag:`, error);
            }
          }
        });

        // Update all icon-related tags with the community's icon
        try {
          const communityIcon = this.faviconURL;
          if (communityIcon) {
            // Set main favicons
            ['icon', 'shortcut icon'].forEach(rel => {
              let element = document.querySelector(`link[rel="${rel}"]`);
              if (!element) {
                element = document.createElement('link');
                element.rel = rel;
                document.head.appendChild(element);
              }
              element.href = communityIcon;
            });

            // Set apple touch icons for different sizes
            const appleSizes = ['57x57', '60x60', '72x72', '76x76', '114x114', '120x120', '144x144', '152x152', '180x180'];
            appleSizes.forEach(size => {
              let element = document.querySelector(`link[rel="apple-touch-icon"][sizes="${size}"]`);
              if (!element) {
                element = document.createElement('link');
                element.rel = 'apple-touch-icon';
                element.sizes = size;
                document.head.appendChild(element);
              }
              element.href = communityIcon;
            });

            // Set Android and other platform icons
            const otherIcons = {
              '192x192': 'image/png',
              '32x32': 'image/png',
              '96x96': 'image/png',
              '16x16': 'image/png'
            };

            Object.entries(otherIcons).forEach(([size, type]) => {
              let element = document.querySelector(`link[rel="icon"][sizes="${size}"]`);
              if (!element) {
                element = document.createElement('link');
                element.rel = 'icon';
                element.sizes = size;
                element.type = type;
                document.head.appendChild(element);
              }
              element.href = communityIcon;
            });

            // Set manifest
            let manifestLink = document.querySelector('link[rel="manifest"]');
            if (!manifestLink) {
              manifestLink = document.createElement('link');
              manifestLink.rel = 'manifest';
              manifestLink.href = '/manifest.json';
              document.head.appendChild(manifestLink);
            }
          }
        } catch (error) {
          console.error('Error setting community icon:', error);
        }

        // Set meta tags
        this.$updateMetaTags({
          title: this.metadataTitle,
          favicon: this.faviconURL,
          description: this.metadataDescription,
          keywords: this.metadataKeywords,
          googleSiteVerification: this.metadataGoogleSiteVerification
        });
      } catch (error) {
        console.error('Error in setMetadata:', error);
      }
    },

    translate(field) {
      return translateTranslationTable(this.currentLocale, field)
    }
  },

  provide() {
    return {
      authApiUrl: this.authApiUrl,
      community: () => this.community,
      // Provide the common computed properties
      isIntercontinentalAlliance: () => this.isIntercontinentalAlliance,
      isNexos: () => this.isNexos,
      isCoeic: () => this.isCoeic,
      isInhospitecnia: () => this.isInhospitecnia,
      isConexionEspana: () => this.isConexionEspana,
      supportEmail: () => this.supportEmail
    }
  }
}
</script> 